import React, { useState, useEffect } from "react";
import {
  Box,
  VStack,
  HStack,
  Text,
  Spinner,
  Alert,
  AlertIcon,
  Tooltip,
  Button,
} from "@chakra-ui/react";
import { RepeatIcon } from "@chakra-ui/icons";
import {
  ScheduledJobType,
} from "build-ts/schedule";
import { Header } from "build-ts/metadata";
import {
  getScheduledJobRunsByJobId,
  deleteScheduledJobRun,
} from "services/ScheduleServices";
import showToast from "hooks/useCustomToast";
import JobRunCard from "containers/schedule/JobRunCard";

interface JobRunsViewerProps {
  selectedJob: Header | undefined;
}

// Main JobRuns component
const JobRunsViewer: React.FC<JobRunsViewerProps> = ({
  selectedJob
}) => {
  const [isLoading, setIsLoading] = useState(true);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [jobRuns, setJobRuns] = useState<Header[]>([]);

  const fetchJobRuns = async (selectedJobId: string) => {
    setIsLoading(true);
    const response = await getScheduledJobRunsByJobId(selectedJobId);
    if (response) {
      setJobRuns(response);
    }
    setIsLoading(false);
  };

  useEffect(() => {
    if (selectedJob?.id) {
      fetchJobRuns(selectedJob?.id);
    }
  }, [selectedJob?.id]);

  const handleRefresh = async () => {
    if (!selectedJob?.id) return;
    setIsRefreshing(true);
    await fetchJobRuns(selectedJob.id);
    setIsRefreshing(false);
    showToast("Job runs refreshed successfully", "", "success");
  };

  const handleDeleteRun = async (runId: string) => {
    const result = await deleteScheduledJobRun(runId);
    if (result) {
      setJobRuns((prev) => prev.filter((run) => run.id !== runId));
      showToast("Job run deleted successfully", "", "success");
      // fetch job runs again
      fetchJobRuns(selectedJob?.id || "");
    }
  };

  if (isLoading) {
    return (
      <Box textAlign="center" py={8}>
        <Spinner size="lg" />
        <Text mt={4}>Loading job runs...</Text>
      </Box>
    );
  }

  return (
    <VStack spacing={2} align="stretch" h="100%">
      <HStack justify="space-between" align="center" flexShrink={0}>
        <Text fontSize="sm" color="fg.default" mb={0} ml={1} fontWeight="700">
          {jobRuns.length > 0 ? `Total runs: ${jobRuns.length}` : "No runs found"}
        </Text>
        <Tooltip label="Refresh runs" placement="top">
          <Button
            leftIcon={<RepeatIcon />}
            size="sm"
            colorScheme="gray"
            variant="outline"
            isLoading={isRefreshing}
            onClick={handleRefresh}
          >
            Refresh
          </Button>
        </Tooltip>
      </HStack>
      {/* Job Runs List */}
      {jobRuns.length === 0 ? (
        <Alert status="info">
          <AlertIcon />
          No job runs found for this scheduled job.
        </Alert>
      ) : (
        <VStack spacing={1} align="stretch" h="100%">
          <Box
            flex={1}
            overflowY="auto"
            maxH="600px"
            pr={2}
            css={{
              "&::-webkit-scrollbar": {
                width: "6px",
              },
              "&::-webkit-scrollbar-track": {
                background: "#f1f1f1",
                borderRadius: "3px",
              },
              "&::-webkit-scrollbar-thumb": {
                background: "#c1c1c1",
                borderRadius: "3px",
              },
              "&::-webkit-scrollbar-thumb:hover": {
                background: "#a8a8a8",
              },
            }}
          >
            {/* Table Header */}
            <HStack
              spacing={4}
              px={4}
              py={3}
              bg="gray.50"
              borderRadius="md"
              mb={2}
              fontWeight="600"
              fontSize="sm"
              color="gray.700"
            >
              <Box flex="2">
                <Text fontSize="md" fontWeight="600" mb={0} color="gray.700" textAlign="left">Run Date</Text>
              </Box>
              <Box flex="1">
                <Text fontSize="md" fontWeight="600" mb={0} color="gray.700" textAlign="left">Status</Text>
              </Box>
              <Box flex="1">
                <Text fontSize="md" fontWeight="600" mb={0} color="gray.700" textAlign="left">Visible</Text>
              </Box>
              <Box flex="1">
                {/* Actions Buttons */}
              </Box>
            </HStack>

            {/* Table Rows */}
            <VStack spacing={0} align="stretch">
              {jobRuns?.map((jobRun) => (
                <JobRunCard
                  key={jobRun.id}
                  jobRun={jobRun}
                  scheduledJobType={
                    ScheduledJobType.ANALYST_JOB
                  }
                  onDelete={() => {
                    handleDeleteRun(jobRun?.id || "");
                  }}
                />
              ))}
            </VStack>
          </Box>
        </VStack>
      )}
    </VStack>
  );
};

export default JobRunsViewer;
